import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Routes, Route, useParams, useNavigate, Navigate } from 'react-router-dom';
import { User, Package, MapPin, LogOut, Download, ChevronRight, ChevronLeft, Printer, Heart } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiMyOrders, apiGetOrder } from '../services/auth';
import Wishlist from './Wishlist';
import AddressBook from './AddressBook';
import useSeo from '../hooks/useSeo';
import { API_BASE } from '../services/http';

const asAbs = (url) => {
  if (!url) return url;
  if (url.startsWith('http')) return url;
  const base = API_BASE.replace(/\/api$/, '');
  return `${base}/storage/${url}`;
};

function Sidebar({ onLogout }) {
  const link = ({ isActive }) =>
    `flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
      isActive ? 'bg-[#1e3a2b] text-white' : 'text-neutral-700 hover:bg-neutral-100'
    }`;

  const handleLogoutClick = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };

  return (
    <aside
      data-testid="account-sidebar"
      className="bg-white rounded-xl border border-neutral-200 p-2 lg:p-3 flex lg:flex-col lg:gap-1 h-fit overflow-x-auto lg:overflow-visible -mx-4 lg:mx-0 px-4 lg:px-3"
    >
      <NavLink to="/account" end className={link}><User className="w-4 h-4" /> Profile</NavLink>
      <NavLink to="/account/orders" className={link}><Package className="w-4 h-4" /> Orders</NavLink>
      <NavLink to="/account/wishlist" className={link}><Heart className="w-4 h-4" /> Wishlist</NavLink>
      <NavLink to="/account/addresses" className={link}><MapPin className="w-4 h-4" /> Addresses</NavLink>
      <button onClick={handleLogoutClick} className="lg:mt-2 flex items-center gap-2 lg:gap-3 px-3 lg:px-4 py-2 lg:py-2.5 rounded-md text-sm font-medium whitespace-nowrap text-red-600 hover:bg-red-50 transition-colors">
        <LogOut className="w-4 h-4" /> Log out
      </button>
    </aside>
  );
}

function Profile() {
  const { user } = useAuth();
  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-6">
      <h2 className="font-serif text-2xl text-neutral-900 mb-4">My Profile</h2>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div><dt className="text-neutral-500">Full name</dt><dd className="font-medium">{user.name}</dd></div>
        <div><dt className="text-neutral-500">Email</dt><dd className="font-medium">{user.email}</dd></div>
        <div><dt className="text-neutral-500">Mobile</dt><dd className="font-medium">{user.phone}</dd></div>
        <div><dt className="text-neutral-500">Member since</dt><dd className="font-medium">{new Date(user.created_at || Date.now()).toLocaleDateString()}</dd></div>
      </dl>
    </div>
  );
}

function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiMyOrders(user.id).then(list => { setOrders(list); setLoading(false); });
  }, [user.id]);

  if (loading) return <div className="bg-white rounded-xl border p-6 text-neutral-500">Loading orders…</div>;
  if (!orders.length) return (
    <div className="bg-white rounded-xl border p-10 text-center">
      <Package className="w-10 h-10 mx-auto text-neutral-400" />
      <h3 className="mt-3 font-serif text-xl">No orders yet</h3>
      <p className="text-sm text-neutral-600 mt-1">Your placed orders will appear here.</p>
      <Link to="/collection/all" className="inline-block mt-4 bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-6 py-2.5 rounded-md font-semibold">Browse products</Link>
    </div>
  );

  const statusStyle = (s) => ({
    pending:   'bg-amber-100 text-amber-800',
    confirmed: 'bg-green-100 text-green-800',
    processing:'bg-blue-100 text-blue-800',
    shipped:   'bg-indigo-100 text-indigo-800',
    delivered: 'bg-emerald-100 text-emerald-800',
    cancelled: 'bg-red-100 text-red-800',
  })[s] || 'bg-neutral-100 text-neutral-700';

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl text-neutral-900">My Orders</h2>
      {orders.map(o => (
        <Link key={o.id} to={`/account/orders/${o.id}`} className="block bg-white rounded-xl border border-neutral-200 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-sm text-neutral-500">Order</div>
              <div className="font-mono font-semibold text-neutral-900">#{o.order_number}</div>
            </div>
            <div className="text-sm">
              <div className="text-neutral-500">Placed</div>
              <div>{new Date(o.created_at).toLocaleDateString()}</div>
            </div>
            <div className="text-sm">
              <div className="text-neutral-500">Items</div>
              <div>{o.items.length}</div>
            </div>
            <div className="text-sm">
              <div className="text-neutral-500">Total</div>
              <div className="font-bold text-neutral-900">Rs. {o.total.toLocaleString('en-IN')}</div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusStyle(o.status)}`}>{o.status}</span>
            <ChevronRight className="w-5 h-5 text-neutral-400" />
          </div>
        </Link>
      ))}
    </div>
  );
}

function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { apiGetOrder(id).then(o => { setOrder(o); setLoading(false); }); }, [id]);

  if (loading) return <div className="bg-white rounded-xl border p-6 text-neutral-500">Loading…</div>;
  if (!order) return <div className="bg-white rounded-xl border p-10 text-center">Order not found. <Link to="/account/orders" className="text-[#1e3a2b] underline">Back</Link></div>;

  const downloadInvoice = () => {
    const html = buildInvoiceHtml(order);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${order.order_number}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printInvoice = () => {
    const w = window.open('', 'invoice', 'width=900,height=800');
    w.document.write(buildInvoiceHtml(order));
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  return (
    <div className="space-y-6">
      <button onClick={() => nav('/account/orders')} className="text-sm text-neutral-700 hover:text-[#1e3a2b] flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to orders
      </button>

      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-sm text-neutral-500">Order number</div>
            <div className="font-mono font-bold text-lg">#{order.order_number}</div>
            <div className="text-sm text-neutral-600 mt-1">Placed on {new Date(order.created_at).toLocaleString()}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={printInvoice} className="inline-flex items-center gap-2 border border-[#1e3a2b] text-[#1e3a2b] hover:bg-[#1e3a2b] hover:text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors">
              <Printer className="w-4 h-4" /> Print invoice
            </button>
            {/* <button onClick={downloadInvoice} className="inline-flex items-center gap-2 bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors">
              <Download className="w-4 h-4" /> Download invoice
            </button> */}
          </div>
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-neutral-500">Status</div>
            <div className="mt-0.5 capitalize font-semibold text-neutral-900">{order.status}</div>
          </div>
          <div>
            <div className="text-neutral-500">Payment</div>
            <div className="mt-0.5 capitalize font-semibold text-neutral-900">{order.payment_method} • {order.payment_status}</div>
          </div>
          <div>
            <div className="text-neutral-500">Total</div>
            <div className="mt-0.5 font-bold text-lg text-neutral-900">Rs. {order.total.toLocaleString('en-IN')}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-6">
        <h3 className="font-serif text-xl mb-4">Items</h3>
        <div className="divide-y">
          {order.items.map((it, i) => (
            <div key={i} className="py-3 flex items-center gap-3">
              <img src={asAbs(it.image)} alt="" className="w-16 h-16 rounded-md object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-neutral-900 line-clamp-1">{it.name || it.title?.split('|')[0]}</div>
                <div className="text-sm text-neutral-500">{it.size} • Qty {it.qty}</div>
              </div>
              <div className="font-semibold">Rs. {(it.unit_price ?? it.price).toLocaleString('en-IN')}</div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-neutral-200 space-y-1 text-sm max-w-xs ml-auto">
          <div className="flex justify-between"><span>Subtotal</span><span>Rs. {order.subtotal.toLocaleString('en-IN')}</span></div>
          {/* <div className="flex justify-between"><span>Shipping</span><span>Rs. {order.shipping.toLocaleString('en-IN')}</span></div> */}
          <div className="flex justify-between"><span>Shipping</span><span>Rs. 0</span></div>
          <div className="flex justify-between font-bold text-neutral-900 text-lg pt-2 border-t"><span>Total</span><span>Rs. {order.total.toLocaleString('en-IN')}</span></div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 p-6 text-sm">
        <h3 className="font-serif text-xl mb-3">Shipping address</h3>
        <p className="font-medium text-neutral-900">{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
        <p className="text-neutral-700">{order.shipping_address.phone} • {order.shipping_address.email}</p>
        <p className="text-neutral-700">{order.shipping_address.address_line1 || order.shipping_address.address} {order.shipping_address.apartment}</p>
        <p className="text-neutral-700">{order.shipping_address.city}, {order.shipping_address.state} – {order.shipping_address.pincode}</p>
      </div>
    </div>
  );
}

function Addresses() {
  return <AddressBook />;
}

function buildInvoiceHtml(o) {
  const rows = o.items.map(it => `
    <tr>
      <td>${it.name || (it.title || '').split('|')[0]}</td>
      <td>${it.size || '-'}</td>
      <td style="text-align:right">${it.qty}</td>
      <td style="text-align:right">Rs. ${(it.unit_price ?? it.price).toLocaleString('en-IN')}.00</td>
      <td style="text-align:right">Rs. ${((it.unit_price ?? it.price) * it.qty).toLocaleString('en-IN')}.00</td>
    </tr>`).join('');
  const a = o.shipping_address;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${o.order_number}</title>
<style>body{font-family:Inter,Arial,sans-serif;color:#1a1a1a;padding:40px;font-size:14px;}
.wrap{max-width:780px;margin:0 auto;}.head{display:flex;justify-content:space-between;border-bottom:3px solid #1e3a2b;padding-bottom:16px;margin-bottom:24px;}
.brand{font-family:Georgia,serif;font-style:italic;font-size:32px;color:#b8935a;}.brand small{display:block;font-size:10px;letter-spacing:4px;color:#b8935a;font-family:Arial;font-style:normal;margin-top:-4px;}
table{width:100%;border-collapse:collapse;margin-top:8px;}th,td{padding:10px 8px;text-align:left;border-bottom:1px solid #eee;}th{background:#f6f2ea;font-size:12px;color:#555;text-transform:uppercase;letter-spacing:1px;}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-bottom:20px;}.card{border:1px solid #eee;border-radius:6px;padding:14px;}
.totals{margin-left:auto;width:300px;margin-top:12px;}.totals div{display:flex;justify-content:space-between;padding:6px 0;}
.grand{border-top:2px solid #1e3a2b;margin-top:6px;padding-top:10px;font-weight:700;font-size:18px;color:#1e3a2b;}
.foot{margin-top:30px;padding-top:16px;border-top:1px solid #eee;text-align:center;color:#777;font-size:12px;}</style></head><body><div class="wrap">
<div class="head"><div><div class="brand">Victoria Resin Store<small></small></div><div style="margin-top:6px;font-size:12px;color:#666">Shop no 03/ Azad road. Juhu koliwada. Near milton apts. Santacruz West. Mumbai 400049 • Email : dgautam036@gmail.com</div></div>
<div style="text-align:right"><h1 style="margin:0;color:#1e3a2b">INVOICE</h1><div>#${o.order_number}</div><div>Date: ${new Date(o.created_at).toLocaleDateString()}</div></div></div>
<div class="grid"><div class="card"><h3>Bill To</h3><div><b>${a.first_name || ''} ${a.last_name || ''}</b></div><div>${a.phone || ''}</div><div>${a.email || ''}</div></div>
<div class="card"><h3>Ship To</h3><div>${a.address_line1 || a.address || ''}</div><div>${a.city || ''}, ${a.state || ''} – ${a.pincode || ''}</div><div>${a.country || 'India'}</div></div></div>
<table><thead><tr><th>Product</th><th>Size</th><th style="text-align:right">Qty</th><th style="text-align:right">Unit</th><th style="text-align:right">Total</th></tr></thead><tbody>${rows}</tbody></table>
<div class="totals"><div><span>Subtotal</span><span>Rs. ${o.subtotal.toLocaleString('en-IN')}.00</span></div>
<div><span>Shipping</span><span>Rs. ${o.shipping.toLocaleString('en-IN')}.00</span></div>
<div class="grand"><span>Total</span><span>Rs. ${o.total.toLocaleString('en-IN')}.00</span></div></div>
<div style="margin-top:24px;font-size:12px;color:#666"><b>Payment:</b> ${o.payment_method.toUpperCase()} • <b>Status:</b> ${o.payment_status.toUpperCase()}</div>
<div class="foot">Thank you for shopping with Victoria Resin Store.</div></div></body></html>`;
}

export default function Account({ onOpen }) {
  const { user, isAuthed, logout } = useAuth();
  const nav = useNavigate();

  useSeo({
    title: 'My Account',
    description: 'Manage your orders, wishlist and saved addresses.',
    canonical: '/account',
  });

  if (!isAuthed) return <Navigate to="/login" state={{ next: '/account' }} replace />;

  const doLogout = () => { logout(); nav('/'); };

  return (
    <main className="bg-[#f6f2ea] min-h-[calc(100vh-4rem)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-serif text-3xl sm:text-4xl text-neutral-900">Hello, {user.name.split(' ')[0]}</h1>
          <p className="text-sm text-neutral-600 mt-1">Manage your orders, wishlist and profile.</p>
        </div>
        <div className="grid lg:grid-cols-[240px_1fr] gap-6">
          <Sidebar onLogout={doLogout} />
          <div>
            <Routes>
              <Route index element={<Profile />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="wishlist" element={<Wishlist onOpen={onOpen} />} />
              <Route path="addresses" element={<Addresses />} />
            </Routes>
          </div>
        </div>
      </div>
    </main>
  );
}